/**
 * Cross-tab pubsub for same-origin pages — zero server cost.
 *
 * Tabs use this to tell each other "data X has changed, you may want to
 * re-fetch" without polling. Primary path: BroadcastChannel (97% global
 * support). Fallback: a transient localStorage key so the `storage` event
 * fires in every OTHER tab (covers old Safari / private mode quirks).
 *
 * Topics are plain strings; payloads are JSON-serialisable. Subscribers
 * filter on the topic they care about.
 *
 *   import { notify, subscribe } from './dataBus';
 *
 *   // tab B (order edit page) — fire after a successful save:
 *   notify('budget-changed', { departmentId: 3, reason: 'edit' });
 *
 *   // tab A (budget page) — refetch when notified:
 *   const unsub = subscribe('budget-changed', (payload) => fetchReport());
 *   // …later
 *   unsub();
 */

const STORAGE_KEY = 'sfx_data_bus';
const CHANNEL_NAME = 'crewforge-data-bus';

const channel =
  typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel(CHANNEL_NAME)
    : null;

export function notify(topic, payload) {
  if (typeof window === 'undefined') return;
  const msg = { topic, payload, at: Date.now() };
  if (channel) {
    try { channel.postMessage(msg); } catch (_) { /* closed */ }
  }
  // localStorage fallback. Setting + immediately removing fires the
  // `storage` event in OTHER tabs but not this one (browsers suppress
  // self-notification), which is exactly the cross-tab semantics we want.
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(msg));
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    // Private mode / quota / disabled storage — silently ignore.
  }
}

export function subscribe(topic, handler) {
  if (typeof window === 'undefined' || typeof handler !== 'function') {
    return () => {};
  }
  const onMsg = (msg) => {
    if (msg && msg.topic === topic) handler(msg.payload);
  };
  const onChan = (e) => onMsg(e.data);
  const onStor = (e) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try { onMsg(JSON.parse(e.newValue)); } catch (_) { /* malformed */ }
  };
  if (channel) channel.addEventListener('message', onChan);
  window.addEventListener('storage', onStor);
  return () => {
    if (channel) channel.removeEventListener('message', onChan);
    window.removeEventListener('storage', onStor);
  };
}
